import { useState, useEffect } from 'react';
import { Plus, Search, Filter, CheckCircle, Clock, AlertTriangle, XCircle, Paperclip, Upload, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../api/api';
import WorkOrderModal from '../components/WorkOrderModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'Pendiente', class: 'bg-gray-100 text-gray-700', icon: Clock },
  in_progress: { label: 'En Curso', class: 'bg-blue-100 text-blue-700', icon: Clock },
  completed: { label: 'Completada', class: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelada', class: 'bg-red-100 text-red-700', icon: XCircle },
};

const PRIORITY_CONFIG = {
  critical: { label: 'Crítica', class: 'bg-red-100 text-red-800 border border-red-200' },
  high: { label: 'Alta', class: 'bg-orange-100 text-orange-800 border border-orange-200' },
  medium: { label: 'Media', class: 'bg-yellow-100 text-yellow-800 border border-yellow-200' },
  low: { label: 'Baja', class: 'bg-gray-100 text-gray-600' },
};

function AttachmentPanel({ order, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('attachments');
  const [activity, setActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const attachments = order.attachments || [];

  const loadActivity = async () => {
    setLoadingActivity(true);
    try {
      const res = await api.get(`/work-orders/${order.id}/activity`);
      setActivity(res.data);
    } finally { setLoadingActivity(false); }
  };

  useEffect(() => {
    if (activeTab === 'history') loadActivity();
  }, [activeTab]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const uploadRes = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const attFd = new FormData();
      attFd.append('url', `${API_URL}${uploadRes.data.url}`);
      attFd.append('filename', uploadRes.data.filename);
      await api.post(`/work-orders/${order.id}/attachments`, attFd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUpdated();
    } finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async (url) => {
    await api.delete(`/work-orders/${order.id}/attachments?url=${encodeURIComponent(url)}`);
    onUpdated();
  };

  const formatTime = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return ts; }
  };

  const ACTION_ICONS = {
    'OT Creada': '✚',
    'Estado cambiado': '⟳',
    'OT Actualizada': '✎',
    'Adjunto añadido': '📎',
    'Adjunto eliminado': '✗',
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      {/* Mini tabs */}
      <div className="flex gap-1 mb-3">
        {[{ k: 'attachments', l: `Adjuntos (${attachments.length})` }, { k: 'history', l: 'Historial' }].map(t => (
          <button key={t.k} onClick={() => setActiveTab(t.k)} className={`text-[11px] font-medium px-3 py-1 rounded-full border transition-colors ${activeTab === t.k ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`}>{t.l}</button>
        ))}
      </div>

      {activeTab === 'attachments' && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold text-gray-500 flex items-center gap-1"><Paperclip size={11} />Archivos adjuntos</p>
            <label className={`flex items-center gap-1 text-[11px] text-[#16a34a] cursor-pointer hover:underline ${uploading ? 'opacity-50' : ''}`}>
              <Upload size={11} />{uploading ? 'Subiendo...' : 'Añadir archivo'}
              <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.xlsx" />
            </label>
          </div>
          {attachments.length === 0 ? <p className="text-xs text-gray-400 italic">Sin archivos adjuntos</p> : (
            <div className="flex flex-wrap gap-2">
              {attachments.map((att, i) => (
                <div key={i} className="flex items-center gap-1.5 text-[11px] bg-gray-50 border border-gray-200 rounded-lg px-2 py-1 group">
                  <Paperclip size={10} className="text-gray-400 flex-shrink-0" />
                  <a href={att.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline max-w-[150px] truncate">{att.filename}</a>
                  <span className="text-[9px] text-gray-400 whitespace-nowrap">{att.uploaded_by}</span>
                  <button onClick={() => handleDelete(att.url)} className="text-gray-300 hover:text-red-500 ml-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-att-${i}`}><Trash2 size={10} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div>
          {loadingActivity ? <div className="flex justify-center py-4"><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#16a34a]" /></div>
            : activity.length === 0 ? <p className="text-xs text-gray-400 italic">Sin actividad registrada</p>
            : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {activity.map(act => (
                  <div key={act.id} className="flex items-start gap-2 text-xs">
                    <span className="text-sm flex-shrink-0 mt-0.5">{ACTION_ICONS[act.action] || '•'}</span>
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-gray-800">{act.action}</span>
                      {act.detail && <span className="text-gray-500"> — {act.detail}</span>}
                    </div>
                    <div className="text-right flex-shrink-0 text-[10px] text-gray-400 whitespace-nowrap">
                      <div>{act.by_user}</div>
                      <div>{formatTime(act.timestamp)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
        </div>
      )}
    </div>
  );
}

export default function OrdenesTrabajo() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editOrder, setEditOrder] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = () => {
    setLoading(true);
    const params = statusFilter ? `?status=${statusFilter}` : '';
    api.get(`/work-orders${params}`)
      .then(r => setOrders(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [statusFilter]);

  const filtered = orders.filter(o =>
    !search || o.title.toLowerCase().includes(search.toLowerCase()) ||
    o.equipment_name?.toLowerCase().includes(search.toLowerCase()) ||
    o.code.toLowerCase().includes(search.toLowerCase())
  );

  const handleUpdateStatus = async (id, status) => {
    await api.put(`/work-orders/${id}`, { status });
    load();
  };

  const counts = {
    all: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    in_progress: orders.filter(o => o.status === 'in_progress').length,
    completed: orders.filter(o => o.status === 'completed').length,
  };

  return (
    <div className="p-6 md:p-8 page-enter">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Barlow, sans-serif' }}>Órdenes de Trabajo</h1>
          <p className="text-sm text-gray-500 mt-0.5">{orders.length} órdenes registradas</p>
        </div>
        <button
          onClick={() => { setEditOrder(null); setShowModal(true); }}
          className="flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          data-testid="create-wo-btn"
        >
          <Plus size={16} /> Nueva OT
        </button>
      </div>

      {/* Status filters */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {[
          { key: '', label: 'Todas', value: counts.all },
          { key: 'pending', label: 'Pendientes', value: counts.pending },
          { key: 'in_progress', label: 'En Curso', value: counts.in_progress },
          { key: 'completed', label: 'Completadas', value: counts.completed },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setStatusFilter(f.key)}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors flex items-center gap-1.5
              ${statusFilter === f.key ? 'bg-[#16a34a] text-white border-[#16a34a]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}
            data-testid={`filter-${f.key || 'all'}`}
          >
            {f.label}
            <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold
              ${statusFilter === f.key ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`}>
              {f.value}
            </span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar OT..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full max-w-md pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#16a34a]"
          data-testid="search-wo"
        />
      </div>

      {/* Orders list */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#16a34a]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-gray-400 text-sm">
            Sin órdenes de trabajo
          </div>
        ) : (
          filtered.map(order => {
            const statusCfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
            const priorityCfg = PRIORITY_CONFIG[order.priority] || PRIORITY_CONFIG.medium;
            const StatusIcon = statusCfg.icon;
            const isExpanded = expandedId === order.id;
            const attCount = order.attachments?.length || 0;
            return (
              <div
                key={order.id}
                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all p-5"
                data-testid={`wo-card-${order.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-xs font-mono text-gray-400">{order.code}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${priorityCfg.class}`}>
                        {priorityCfg.label}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${order.type === 'corrective' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                        {order.type === 'corrective' ? 'Correctivo' : 'Preventivo'}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm">{order.title}</h3>
                    <p className="text-xs text-gray-500 mt-1">{order.equipment_name} · {order.assigned_to || 'Sin asignar'}</p>
                    {order.description && (
                      <p className="text-xs text-gray-400 mt-1 line-clamp-2">{order.description}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${statusCfg.class}`}>
                      <StatusIcon size={12} />
                      {statusCfg.label}
                    </span>
                    <span className="text-[10px] text-gray-400">
                      {new Date(order.created_at).toLocaleDateString('es-ES')}
                    </span>
                    {/* Quick status actions */}
                    <div className="flex gap-1 flex-wrap justify-end">
                      {order.status === 'pending' && (
                        <button
                          onClick={() => handleUpdateStatus(order.id, 'in_progress')}
                          className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                          data-testid={`start-wo-${order.id}`}
                        >
                          Iniciar
                        </button>
                      )}
                      {order.status === 'in_progress' && (
                        <button
                          onClick={() => handleUpdateStatus(order.id, 'completed')}
                          className="text-[10px] px-2 py-0.5 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                          data-testid={`complete-wo-${order.id}`}
                        >
                          Completar
                        </button>
                      )}
                      <button
                        onClick={() => { setEditOrder(order); setShowModal(true); }}
                        className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                        data-testid={`edit-wo-${order.id}`}
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : order.id)}
                        className="flex items-center gap-0.5 text-[10px] px-2 py-0.5 bg-gray-50 text-gray-500 rounded hover:bg-gray-100 transition-colors"
                        data-testid={`expand-wo-${order.id}`}
                      >
                        <Paperclip size={10} />{attCount > 0 ? attCount : ''}
                        {isExpanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
                      </button>
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <AttachmentPanel order={order} onUpdated={load} />
                )}
              </div>
            );
          })
        )}
      </div>

      {showModal && (
        <WorkOrderModal order={editOrder} onClose={() => setShowModal(false)} onSaved={load} />
      )}
    </div>
  );
}

